import type {
  GhanttBarColorToken,
  GhanttRowValue,
  RowConfig,
  TableCellComponentProps,
} from '@/shared/types/table';
import { cn } from '@/shared/utils/cn';
import {
  getTableCellClasses,
  getTableCellProps,
  handleCellClick,
  getContentAlignClass,
  isFlexColumn,
} from '../../utils/cellStyles';
import {
  useGhanttConfig,
  ghanttBarLayoutPercent,
  ghanttBuildTimelineSlots,
  ghanttDefaultColumnWidthForScale,
  ghanttParseTime,
  ghanttSlotsTimeExtent,
  ghanttSnapSlotWidthPx,
  GhanttSlotStrip,
  type GhanttTimelineScale,
} from '@/shared/ghantt';
import { memo, useLayoutEffect, useMemo, useRef } from 'react';
import type { WheelEvent } from 'react';

const BAR_FILL: Record<GhanttBarColorToken, string> = {
  brand: 'ui-bg-fill-brand',
  success: 'ui-bg-fill-success',
  warning: 'ui-bg-fill-warning',
  danger: 'ui-bg-fill-danger',
  info: 'ui-bg-fill-info',
  gray: 'ui-bg-fill-disabled',
};

function isGhanttRowValue(v: unknown): v is GhanttRowValue {
  return (
    !!v &&
    typeof v === 'object' &&
    !Array.isArray(v) &&
    'bars' in v &&
    Array.isArray((v as GhanttRowValue).bars)
  );
}

/** Firma estable para `memo` cuando el consumidor recrea el objeto `GhanttRowValue` sin cambiar barras. */
function ghanttBarsSignature(rawValue: unknown, value: unknown): string {
  const src = rawValue !== undefined ? rawValue : value;
  if (!isGhanttRowValue(src)) return `x:${String(src)}`;
  const sep = '\x1f';
  return src.bars
    .map(
      (b) =>
        `${b.id}${sep}${String(b.start)}${sep}${String(b.end)}${sep}${b.progress ?? ''}${sep}${b.colorToken ?? ''}${sep}${b.label ?? ''}`,
    )
    .join('\x1e');
}

function ganttCellColumnEqual(
  a: TableCellComponentProps['column'],
  b: TableCellComponentProps['column'],
): boolean {
  return (
    a.id === b.id &&
    a.width === b.width &&
    a.minWidth === b.minWidth &&
    a.maxWidth === b.maxWidth &&
    a.locked === b.locked &&
    a.contentAlign === b.contentAlign
  );
}

function ganttCellPropsAreEqual(
  prev: TableCellComponentProps,
  next: TableCellComponentProps,
): boolean {
  if (prev.state !== next.state) return false;
  if (prev.isRowEditing !== next.isRowEditing) return false;
  if (prev.isLastLocked !== next.isLastLocked) return false;
  if (prev.stickyLeft !== next.stickyLeft) return false;
  if (prev.stickyRight !== next.stickyRight) return false;
  if (prev.isActionsColumn !== next.isActionsColumn) return false;
  if (prev.hasColumnMaxWidthAuto !== next.hasColumnMaxWidthAuto) return false;
  if (prev.className !== next.className) return false;
  if (prev.onCellFocus !== next.onCellFocus) return false;
  if (prev.onChange !== next.onChange) return false;
  if (ghanttBarsSignature(prev.rawValue, prev.value) !== ghanttBarsSignature(next.rawValue, next.value)) {
    return false;
  }
  if (!ganttCellColumnEqual(prev.column, next.column)) return false;
  const pr = prev.row as RowConfig;
  const nr = next.row as RowConfig;
  if (pr.id !== nr.id) return false;
  return true;
}

/**
 * Celda de tabla para `type: 'gantt'`: misma altura y estados que el resto de celdas de la fila.
 * La rejilla sigue el **mismo scroll horizontal** que la cabecera: viewport con barra solo arriba;
 * aquí se aplica `translateX(-scrollLeft)` (sin scroll por fila).
 */
function GanttCellInner({
  value,
  rawValue,
  row,
  column,
  state = 'default',
  className,
  onCellFocus,
  stickyLeft = 0,
  isLastLocked = false,
  isRowEditing,
  hasColumnMaxWidthAuto = false,
}: TableCellComponentProps) {
  const cfg = useGhanttConfig();
  const baseClasses = getTableCellClasses(
    state,
    className,
    column.locked,
    isLastLocked,
    undefined,
    isRowEditing,
    false,
    column.width !== undefined || isFlexColumn(column, hasColumnMaxWidthAuto),
  );
  const cellProps = getTableCellProps(column, stickyLeft, undefined, false, hasColumnMaxWidthAuto);
  const hasFixedWidth = column.width !== undefined || isFlexColumn(column, hasColumnMaxWidthAuto);
  const alignClass = getContentAlignClass(column.contentAlign);

  const data = useMemo(() => {
    const src = rawValue !== undefined ? rawValue : value;
    if (isGhanttRowValue(src)) return src;
    return { bars: [] } satisfies GhanttRowValue;
  }, [rawValue, value]);

  const scale = (cfg?.timeline.scale ?? 'day') as GhanttTimelineScale;
  const isDayScale = scale === 'day';
  const trackMin = cfg?.appearance.trackMinHeightPx ?? 44;
  const trackClass = cfg?.appearance.trackClassName;

  const viewStart = cfg?.timeline.viewStart;
  const viewEnd = cfg?.timeline.viewEnd;
  const includeLeadingFullMonth = cfg?.timeline.includeLeadingFullMonth !== false;

  const slotsFromCtx = cfg?.timelineSlots;
  const slots = useMemo(() => {
    if (slotsFromCtx && slotsFromCtx.length > 0) {
      return slotsFromCtx;
    }
    if (viewStart == null || viewEnd == null) return [];
    return ghanttBuildTimelineSlots(viewStart, viewEnd, scale, {
      includeLeadingFullMonth: isDayScale ? includeLeadingFullMonth : undefined,
    });
  }, [slotsFromCtx, viewStart, viewEnd, scale, isDayScale, includeLeadingFullMonth]);

  const rangeValid =
    !!cfg &&
    (isDayScale
      ? !!cfg.dayAxis && cfg.dayAxis.endExclusiveMs > cfg.dayAxis.startMs
      : Number.isFinite(ghanttParseTime(cfg.timeline.viewStart)) &&
        Number.isFinite(ghanttParseTime(cfg.timeline.viewEnd)) &&
        ghanttParseTime(cfg.timeline.viewEnd) > ghanttParseTime(cfg.timeline.viewStart) &&
        slots.length > 0);

  const barExtent = useMemo(() => {
    if (!cfg) return null;
    if (isDayScale && cfg.dayAxis && cfg.dayAxis.endExclusiveMs > cfg.dayAxis.startMs) {
      return { startMs: cfg.dayAxis.startMs, endExclusiveMs: cfg.dayAxis.endExclusiveMs };
    }
    return ghanttSlotsTimeExtent(slots, scale);
  }, [cfg, isDayScale, slots, scale]);

  const barViewStartMs = barExtent?.startMs ?? NaN;
  const barViewEndMs = barExtent?.endExclusiveMs ?? NaN;

  const slotMinWidthPx =
    cfg?.timeline.columnWidthPx ?? ghanttDefaultColumnWidthForScale(scale);

  const snappedSlotPx =
    slotMinWidthPx != null && slotMinWidthPx > 0 ? ghanttSnapSlotWidthPx(slotMinWidthPx) : undefined;

  const timelineInnerWidthPx =
    snappedSlotPx != null && slots.length > 0 ? slots.length * snappedSlotPx : undefined;

  const hs = cfg?.horizontalScroll;
  const registerBodyTimeline = cfg?.horizontalScroll?.registerBodyTimeline;
  const trackRef = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    const el = trackRef.current;
    if (!el || !registerBodyTimeline) return;
    return registerBodyTimeline(el);
  }, [registerBodyTimeline]);

  /** `wheel` no pasivo: permite `preventDefault` del zoom (React suele registrar la rueda como pasiva en el `td`). */
  useLayoutEffect(() => {
    const el = trackRef.current;
    if (!el || !hs?.onBodyWheel) return;
    const handler = hs.onBodyWheel;
    const listener = (ev: Event) => {
      handler(ev as any);
    };
    el.addEventListener('wheel', listener, { passive: false });
    return () => {
      el.removeEventListener('wheel', listener);
    };
  }, [hs?.onBodyWheel]);

  if (!cfg || !rangeValid) {
    return (
      <td className={baseClasses} {...cellProps} onClick={(e) => handleCellClick(e, onCellFocus)}>
        <div
          className={cn(
            'ui-flex ui-h-full ui-items-center',
            alignClass,
            hasFixedWidth && 'ui-min-w-0 ui-overflow-hidden',
          )}
        >
          <span className="ui-sr-only">Sin configuración de cronograma</span>
        </div>
      </td>
    );
  }

  const rowTyped = row as RowConfig<unknown>;

  return (
    <td
      className={baseClasses}
      {...cellProps}
      onClick={(e) => handleCellClick(e, onCellFocus)}
    >
      <div
        className={cn(
          'ui-flex ui-h-full ui-min-h-0 ui-w-full ui-min-w-0 ui-flex-col ui-justify-stretch',
          alignClass,
          'ui-min-w-0',
        )}
      >
        <div className="ui-flex ui-h-full ui-min-h-0 ui-w-full ui-min-w-0 ui-flex-1 ui-overflow-x-hidden ui-overflow-y-hidden">
          <div
            ref={trackRef}
            className={cn(
              'ui-relative ui-box-border ui-flex ui-h-full ui-min-h-0 ui-shrink-0 ui-bg-transparent',
              trackClass,
            )}
            style={{
              minHeight: Math.min(trackMin, 52),
              width: timelineInnerWidthPx ?? '100%',
              minWidth: timelineInnerWidthPx ?? undefined,
            }}
            aria-label="Cronograma"
          >
            <GhanttSlotStrip
              slots={slots}
              upperSegments={cfg.upperSegments}
              variant="body"
              className="ui-absolute ui-inset-0 ui-z-0"
              slotMinWidthPx={slotMinWidthPx}
            />

            <div className="ui-pointer-events-none ui-absolute ui-inset-0 ui-z-[1] ui-flex ui-items-stretch ui-px-0 ui-py-1">
              {data.bars.map((bar) => {
                const bs = typeof bar.start === 'number' ? bar.start : ghanttParseTime(bar.start);
                const be = typeof bar.end === 'number' ? bar.end : ghanttParseTime(bar.end);
                if (!Number.isFinite(bs) || !Number.isFinite(be) || be <= bs) return null;
                const layout = ghanttBarLayoutPercent(barViewStartMs, barViewEndMs, bs, be);
                if (!layout) return null;
                const fill = BAR_FILL[bar.colorToken ?? 'brand'];
                const progress =
                typeof bar.progress === 'number'
                  ? Math.min(100, Math.max(0, bar.progress))
                  : undefined;

                return (
                  <button
                    key={bar.id}
                    type="button"
                    className={cn(
                      'focus-visible:ui-ring-border-brand ui-pointer-events-auto ui-absolute ui-top-1/2 ui-box-border ui-max-h-[70%] ui-min-h-6 -ui-translate-y-1/2 ui-overflow-hidden ui-rounded-md ui-text-left ui-outline-none focus-visible:ui-ring-2',
                      fill,
                      state === 'selected' && !isRowEditing && 'ui-ring-border-brand ui-ring-1',
                    )}
                    style={{
                      left: `${layout.leftPct}%`,
                      width: `${layout.widthPct}%`,
                    }}
                    aria-label={bar.label ?? bar.id}
                    onClick={(e) => {
                      e.stopPropagation();
                      cfg.events.onBarClick?.({ row: rowTyped, bar });
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        e.stopPropagation();
                        cfg.events.onBarClick?.({ row: rowTyped, bar });
                      }
                    }}
                >
                    {progress !== undefined ? (
                      <span className="ui-relative ui-block ui-h-full ui-w-full">
                        <span
                          className="ui-pointer-events-none ui-absolute ui-inset-y-0 ui-left-0 ui-bg-fill-disabled ui-opacity-50"
                          style={{ width: `${progress}%` }}
                      />
                        {bar.label ? (
                          <span className="ui-relative ui-z-[1] ui-px-1.5 ui-text-body-xs-regular ui-font-body-xs-medium ui-text-on-color">
                            {bar.label}
                          </span>
                        ) : null}
                      </span>
                    ) : bar.label ? (
                      <span className="ui-block ui-truncate ui-px-1.5 ui-text-body-xs-regular ui-font-body-xs-medium ui-text-on-color">
                        {bar.label}
                      </span>
                    ) : null}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </td>
  );
}

export const GanttCell = memo(GanttCellInner, ganttCellPropsAreEqual);
GanttCell.displayName = 'GanttCell';
